import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Term } from '@court-sync/shared';
import { termService } from '../services/term';
import { STALE_TIME } from '../queryClient';
import { queryKeys } from './keys';
import { unwrapApiData, unwrapApiDataOrEmpty } from './unwrap';
import { removeLessonsByTermIdFromCache } from './lessons';

export function useTerms(academyId?: string) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.terms.list(academyId ?? '');

  const { data: terms = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await termService.getTerms(academyId);
      return unwrapApiDataOrEmpty(response);
    },
    enabled: !!academyId,
    staleTime: STALE_TIME.MEDIUM,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.terms.lists() });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: async (data: Omit<Term, 'id' | 'createdAt' | 'updatedAt'>) => {
      const response = await termService.createTerm(data);
      const id = unwrapApiData(response, 'Failed to create term').id;
      const detail = await termService.getTerm(id);
      return unwrapApiData(detail, 'Failed to fetch created term');
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Omit<Term, 'id' | 'createdAt' | 'updatedAt'>>;
    }) => {
      await termService.updateTerm(id, data);
      const detail = await termService.getTerm(id);
      return unwrapApiData(detail, 'Failed to fetch updated term');
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Term[]>(queryKey);
      queryClient.setQueryData<Term[]>(queryKey, (old) =>
        old?.map((term) => (term.id === id ? { ...term, ...data, updatedAt: new Date() } : term))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await termService.deleteTerm(id);
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Term[]>(queryKey);
      queryClient.setQueryData<Term[]>(queryKey, (old) => old?.filter((t) => t.id !== id));
      removeLessonsByTermIdFromCache(id);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: invalidate,
  });

  const getTermById = useCallback((id: string) => terms.find((t) => t.id === id), [terms]);

  const refresh = useCallback(() => refetch(), [refetch]);

  const fetchTerms = useCallback(
    async (id?: string, options?: { force?: boolean }) => {
      const key = queryKeys.terms.list(id ?? academyId ?? '');
      if (options?.force) {
        await queryClient.invalidateQueries({ queryKey: key });
      }
      await queryClient.fetchQuery({
        queryKey: key,
        queryFn: async () => {
          const response = await termService.getTerms(id ?? academyId);
          return unwrapApiDataOrEmpty(response);
        },
        staleTime: STALE_TIME.MEDIUM,
      });
    },
    [academyId, queryClient]
  );

  return {
    terms,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    fetchTerms,
    invalidate,
    createTerm: createMutation.mutateAsync,
    updateTerm: (id: string, data: Parameters<typeof updateMutation.mutateAsync>[0]['data']) =>
      updateMutation.mutateAsync({ id, data }),
    deleteTerm: deleteMutation.mutateAsync,
    getTermById,
  };
}
